<template>
  <div class="select-submissions">
    <div class="filters d-flex align-items-center mb-3">
      <div
        v-for="filter in filters"
        :key="filter.key"
        :data-testid="`filter-dropdown-${filter.key}`"
      >
        <filter-dropdown
          ref="filter-dropdown"
          :button-label="filter.title"
          :active="selectedFilters.includes(filter.key)"
          :title="filter.title"
        >
          <component
            :is="filter.component"
            v-model="filter.value"
            class="filter"
            :items="filter.items"
            :placeholder="filter.title"
            value-is-id
            no-label
            @input="applyFilter(filter.key)"
          ></component>
        </filter-dropdown>
      </div>
      <eg-button
        v-if="selectedFilters.length > 0"
        size="small"
        icon="close"
        class="clear-filters ml-2"
        @click="clearAllFilters"
      >
        {{ $t("imports.clearFilters") }}
      </eg-button>

      <div v-if="showOpenInLms && assignmentLink" class="ml-auto">
        <eg-button
          size="small"
          icon="share-arrow-sqaure"
          class="clear-filters ml-2"
          @click="openLink"
        >
          {{ $t("imports.openInGoogleClassroom") }}
        </eg-button>
      </div>
    </div>

    <eg-selectable-table
      class="submissions-table w-100"
      selectable
      split-select
      :value="value"
      :loading="loading"
      :initial-loading="false"
      :items="filteredSubmissions"
      :total="filteredSubmissions.length"
      :limit="filteredSubmissions.length + 1"
      :fields="fields"
      :get-disabled="(item) => item.disabled"
      @input="selectSubmissions"
    >
      <template #cell(state)="{ item }">
        <eg-b-badge :id="item.id" pill class="status-badge imported">
          <span class="badge-text">{{ getStateBadgeText(item.state) }}</span>
        </eg-b-badge>
      </template>
      <template #cell(imported)="{ item }">
        <cell-status
          :item="item"
          :badges-popover="getPopoverImportStatus(item)"
          data-testid="cell-status"
        />
      </template>
      <template #empty-state>
        <empty-state key="filtered" :title="emptyStateMessage" class="m-auto">
        </empty-state>
      </template>
    </eg-selectable-table>
  </div>
</template>

<script>
import formatDate from "@/utils/formatDate";
import EgSelectableTable from "@/components/global/eg_selectable_table";
import EmptyState from "@/components/global/empty_state.vue";
import CellStatus from "@/components/essay/table/cell_status.vue";
import FilterDropdown from "@/components/essay/filters/filter_dropdown.vue";
import FilterSelect from "@/components/filter-select";
import {
  GC_SUBMISSION_STATES,
  GC_SUBMISSION_STATES_LABELS,
  SUBMISSION_IMPORT_FAILURE_REASONS,
} from "@/constants";

const getFilters = (t) => ({
  state: {
    title: t("imports.submissionState"),
    key: "state",
    value: null,
    prepareValue: function (value) {
      return value.id;
    },
    component: "filter-select",
    items: Object.keys(GC_SUBMISSION_STATES_LABELS)
      .filter((labelKey) =>
        [
          GC_SUBMISSION_STATES.NEW,
          GC_SUBMISSION_STATES.CREATED,
          GC_SUBMISSION_STATES.TURNED_IN,
          GC_SUBMISSION_STATES.RECLAIMED_BY_STUDENT,
        ].includes(labelKey)
      )
      .map((labelKey) => ({
        id: labelKey,
        name: t(GC_SUBMISSION_STATES_LABELS[labelKey]),
      })),
  },
  status: {
    title: t("imports.importStatus"),
    key: "status",
    value: null,
    prepareValue: function (value) {
      return value.id;
    },
    component: "filter-select",
    items: [
      { id: "Imported", name: t("imports.imported") },
      { id: "NotImported", name: t("imports.notImported") },
    ],
  },
});

export default {
  name: "ImportsSelectSubmissionsTable",
  components: {
    EmptyState,
    CellStatus,
    EgSelectableTable,
    FilterSelect,
    FilterDropdown,
  },

  props: {
    value: {
      type: Array,
      default: () => [],
    },
    submissions: {
      type: Array,
      default: () => [],
    },
    assignmentLink: {
      type: String,
      default: () => null,
    },
    showOpenInLms: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    const filtersToDisplay = getFilters(this.$t.bind(this));
    const fields = [
      {
        key: "essayName",
        label: this.$t("imports.essayName"),
        formatter: (value, key, item) => {
          return item.name;
        },
      },
      {
        key: "student",
        label: this.$t("imports.studentName"),
        formatter: (value, key, item) => {
          const studentName = item.student.name;

          return studentName?.fullName || studentName?.firstName || "Student";
        },
      },
      {
        key: "creationTime",
        label: this.$t("imports.submittedOn"),
        formatter: (value) => {
          return value ? formatDate(value) : "-";
        },
      },
      {
        key: "updateTime",
        label: this.$t("imports.lastUpdated"),
        formatter: (value) => {
          return value ? formatDate(value) : "-";
        },
      },
      {
        key: "imported",
        label: this.$t("imports.importStatus"),
      },
    ];

    if (this.showOpenInLms) {
      fields.splice(fields.length - 1, 0, {
        key: "state",
        label: this.$t("imports.state"),
      });
    } else {
      delete filtersToDisplay.state;
    }

    return {
      filters: filtersToDisplay,
      selectedFilters: [],
      fields,
    };
  },

  computed: {
    emptyStateMessage() {
      if (this.loading) {
        return this.$t("common.loading");
      }

      return this.$t("imports.noSubmissionsFound");
    },

    filteredSubmissions() {
      if (this.selectedFilters.length === 0) {
        return this.submissions;
      }

      return this.submissions.filter((submission) => {
        return this.selectedFilters.every((filterKey) => {
          const filter = this.filters[filterKey];
          const prepareValue = filter.prepareValue;

          return prepareValue(filter.value) === submission[filterKey];
        });
      });
    },
  },

  watch: {
    loading: {
      immediate: true,
      handler(isLoading) {
        if (isLoading) {
          this.clearAllFilters();
        }
      },
    },
  },

  methods: {
    clearAllFilters() {
      this.selectedFilters = [];
      Object.keys(this.filters).forEach((key) => {
        this.filters[key].value = null;
      });
    },

    applyFilter(key) {
      const index = this.selectedFilters.findIndex(
        (filterKey) => filterKey === key
      );
      // reset selected submissions
      this.selectSubmissions([]);

      // add flag that filter is active, need do only once
      if (index === -1) {
        this.selectedFilters.push(key);
      }
    },

    selectSubmissions(submissions) {
      this.$emit("input", submissions);
    },

    goToEssay(essayId) {
      this.$router.push(`/essay/${essayId}`);
      this.$emit("essay-clicked", essayId);
    },

    getStateBadgeText(state) {
      return this.$t(
        GC_SUBMISSION_STATES_LABELS[state] ||
          GC_SUBMISSION_STATES_LABELS.STATE_UNSPECIFIED
      );
    },

    getPopoverImportStatus(item) {
      const key = SUBMISSION_IMPORT_FAILURE_REASONS[item.reason];
      if (!key) return null;
      if (item.reason === "noGoogleDocsFiles") {
        return `${this.$t(key)} <a href="/grading/manual-upload">${this.$t(
          "imports.gradeManually"
        )}</a>`;
      }
      return this.$t(key);
    },

    openLink() {
      window.open(this.assignmentLink, "_blank");
    },
  },
};
</script>

<style lang="scss" scoped>
.filter {
  &::v-deep {
    .btn {
      width: auto;
      background: var(--color-surface-neutral-container-1, #f6f8fa);
      border-color: var(--color-stroke-neutral-default, #e5ebf0);
    }
  }
}
.submissions-table {
  &::v-deep {
    .selectable-table .table.b-table-sticky-header {
      border: 1px solid var(--color-stroke-neutral-default);
      border-radius: var(--radius-rd-400);
      border-collapse: separate;
    }
    .table thead tr th.table-b-table-default {
      background: var(--color-surface-neutral-container-1);
      color: var(--color-text-neutral-secondary);
      font-weight: var(--typography-font-weight-fw-600);
      font-size: var(--typography-font-size-fs-200);
    }
    tbody tr td {
      font-size: var(--typography-font-size-fs-200);
    }
  }
}
.status-badge {
  display: inline-flex;
  height: 24px;
  padding: 3px 8px;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  border-radius: 12px;
  width: auto;
  font-size: var(--typography-font-size-fs-200);
  font-style: normal;
  font-weight: var(--typography-font-weight-fw-500, 500);
  line-height: 1;
  pointer-events: none;

  &.imported {
    background: var(--color-surface-neutral-container-2);
    color: var(--color-text-neutral-neutral);
  }
}
.badge-text {
  white-space: nowrap;
}
</style>
